import { type ReactNode } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/features/auth'

interface ProtectedRouteProps {
    children: ReactNode
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const { isAuthenticated, isLoading, initError } = useAuthStore()

    const centeredBox = (content: ReactNode) => (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                gap: 2,
                background: theme.palette.background.default,
            }}
        >
            {content}
        </Box>
    )

    if (initError) {
        return centeredBox(
            <>
                <Typography sx={{ fontWeight: 700 }}>{t('authErrorTitle')}</Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '13px' }}>{initError}</Typography>
            </>
        )
    }

    if (isLoading || !isAuthenticated) {
        return centeredBox(
            <>
                <CircularProgress />
                <Typography>
                    {isLoading ? t('loading') : t('redirectingToLogin')}
                </Typography>
            </>
        )
    }

    return children
}

export default ProtectedRoute
